import React, { useEffect, useMemo } from "react";

/**
 * The hand-off from the browser to the app.
 *
 * Every email link lands in a browser, and everything a parent or an athlete
 * does afterwards belongs in the app. Left alone they end up juggling the two —
 * verifying in Safari, then hunting for the app, then signing in again.
 *
 * So: offer the store their phone actually uses, first and largest, with the
 * other two kept as quiet alternatives. On an `attempt`, try the app's own URL
 * scheme first — if it is installed, the page never finishes rendering and they
 * are simply in the app.
 */

export const STORES = {
  ios: "https://apps.apple.com/ro/app/igrowth/id6767212760",
  android: "https://play.google.com/store/apps/details?id=com.sportiveducat.app",
};

export type Platform = "ios" | "android" | "other";

export function detectPlatform(ua: string = navigator.userAgent || ""): Platform {
  if (/android/i.test(ua)) return "android";
  // iPadOS 13+ reports itself as a Mac; the touch point count gives it away.
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/macintosh/i.test(ua) && typeof document !== "undefined" && navigator.maxTouchPoints > 1) {
    return "ios";
  }
  return "other";
}

const BADGES = {
  ios: "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/ro-ro?size=250x83",
  android: "https://play.google.com/intl/en_us/badges/static/images/badges/ro_badge_web_generic.png",
};

export default function AppHandoff({
  path = "/app",
  attempt = false,
  tone = "dark",
  title = "Continuă în aplicație",
  subtitle = "Instalează iGROWth pe telefon — acolo se întâmplă tot.",
}: {
  /** Where inside the app to land, when it is installed. */
  path?: string;
  /** Try to open the installed app immediately. */
  attempt?: boolean;
  tone?: "dark" | "light";
  title?: string;
  subtitle?: string;
}) {
  const platform = useMemo(() => detectPlatform(), []);

  useEffect(() => {
    if (!attempt || platform === "other") return;
    // A custom scheme does nothing when the app is missing — which is exactly
    // the behaviour wanted here, because the store badges are already on
    // screen behind this attempt.
    const target = `sportiveducat://${path.replace(/^\//, "")}`;
    const timer = setTimeout(() => {
      window.location.href = target;
    }, 400);
    return () => clearTimeout(timer);
  }, [attempt, path, platform]);

  const dark = tone === "dark";
  const order: ("ios" | "android")[] = platform === "android" ? ["android", "ios"] : ["ios", "android"];

  return (
    <div
      className={`p-5 rounded-2xl border ${
        dark ? "bg-slate-800/50 border-slate-700/50" : "bg-white border-slate-200"
      }`}
    >
      <p className={`text-base font-bold ${dark ? "text-white" : "text-slate-800"}`}>{title}</p>
      <p className={`mt-1 text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>{subtitle}</p>

      <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
        {order.map((store, i) => (
          <a
            key={store}
            href={STORES[store]}
            target="_blank"
            rel="noreferrer"
            // The phone's own store first and at full size; the other one is
            // there for the parent installing it on someone else's phone.
            className={i === 0 ? "" : "opacity-70 hover:opacity-100 transition-opacity"}
          >
            <img
              src={BADGES[store]}
              alt={store === "ios" ? "Descarcă din App Store" : "Disponibil în Google Play"}
              className={i === 0 ? "h-12" : "h-10"}
            />
          </a>
        ))}
      </div>

      {/* Deliberately small and plain: the app is the product, the browser is
          the fallback for someone who cannot install anything right now.
          As a third button of equal weight, it was picked by people who
          should not have picked it. */}
      <p className={`mt-3 text-xs text-center ${dark ? "text-slate-500" : "text-slate-400"}`}>
        Nu poți instala acum?{" "}
        <a href={path} className="underline hover:no-underline">
          Deschide în browser
        </a>
      </p>
    </div>
  );
}
